import React, { FC, memo, useContext, useMemo } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import PdfTableComponent from '../PdfComponents/PdfTable';
import { TravelFileServicePreviewNamesEnum } from '../../../api/services/constants/travelFileService';
import styles from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfTotalBox from '../PdfComponents/PdfTotalBox';
import PdfTitleTextBox from '../PdfComponents/PdfTitleTextBox';
import { PdfRemarksSection } from '../PdfComponents/PdfSection';
import PdfSupplierDetails from '../PdfComponents/PdfSupplierDetails';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';
import { VouchersPdfPropsType } from './voucherHelpers';

export interface PdfTotalBoxDataItem {
    label: string;
    value: number;
}

const VoucherServicePdfDocument: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);

    const {
        info: {
            dueDate,
            supplierName,
            travelFileNumber,
            remark,
            pdfInfo: {
                companyInfo,
                travelFileServiceTypePreviewName = '',
                supplierDetails,
                userName,
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl,
                code1code2,
                voucherDetailsColumns,
                voucherDetailsRows,
                voucherPassengersColumns,
                voucherPassengersRows,
                passengersSummaryLine,
                hidePassengersTable,
                pdfBodyBoxData,
                pdfTotalBoxData,
                voucherCertification = 'Draft',
                provideArrangeForText,
                detailsText,
                supplierAddressText,
                priceComponentRows,
                hotelDetailsColumns,
                hotelDetailsRows,
                hotelGuestColumns,
                hotelGuestRows,
                hotelPassengerCount,
                supplierInvoiceNumber,
                splitFromNote
            }
        },
        number: voucherNumber,
        type: voucherType,
        isPaid,
        createdAt
    } = props;

    const bodyDetails = useMemo(() => {
        // Same block in every service type — the builder decides whether it shows
        // (per-type default, overridden by the "Show Passengers Table in Voucher
        // PDF" checkbox). Hotel/package and general used to omit it entirely, so
        // ticking the box there had no effect.
        const passengersTable = !hidePassengersTable && voucherPassengersRows?.length > 0 && (
            <>
                <View style={[styles.section, styles.marginBottom10px]}>
                    <PdfTableComponent
                        columns={voucherPassengersColumns}
                        rows={voucherPassengersRows}
                        summaryLine={passengersSummaryLine}
                    />
                </View>

                <View style={[styles.section, styles.marginBottom10px]}>
                    <Text style={[styles.littleText]}>
                        Total Passengers: {voucherPassengersRows?.length}
                    </Text>
                </View>
            </>
        );

        switch (travelFileServiceTypePreviewName) {
            case TravelFileServicePreviewNamesEnum.GUIDE:
                return (
                    <>
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.littleText, styles.marginBottom4px]}>
                                {`Please provide/arrange for: ${
                                    provideArrangeForText || pdfBodyBoxData?.[1]?.value || ''
                                } `}
                            </Text>
                            <Text style={[styles.littleText, styles.marginBottom4px]}>Service as follows:</Text>
                            <PdfTableComponent columns={voucherDetailsColumns} rows={voucherDetailsRows} />
                        </View>

                        {passengersTable}
                    </>
                );
            case TravelFileServicePreviewNamesEnum.FLIGHT:
                return (
                    <>
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.littleText, styles.marginBottom4px]}>
                                In Exchange for this Voucher, Please Issue the Following Tickets:
                            </Text>
                            <PdfTableComponent columns={voucherDetailsColumns} rows={voucherDetailsRows} />
                        </View>

                        {passengersTable}
                    </>
                );
            case TravelFileServicePreviewNamesEnum.PACKAGE:
            case TravelFileServicePreviewNamesEnum.HOTEL:
                return (
                    <>
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.littleText, styles.marginBottom4px]}>
                                Please confirm the following reservation:
                            </Text>
                            {voucherDetailsRows?.[0] && (() => {
                                const row = voucherDetailsRows[0];
                                return (
                                    <View style={{ marginBottom: 8 }}>
                                        <View style={{ flexDirection: 'row', alignItems: 'baseline', marginBottom: 6 }}>
                                            <Text style={{ fontSize: 11, fontWeight: 'bold', marginRight: 15 }}>{row.titleName || ''}</Text>
                                            {!!row.titleAddress && <Text style={{ fontSize: 9, color: '#666666' }}>|     {row.titleAddress}</Text>}
                                        </View>
                                        {row.details?.map((d: any, idx: number) => (
                                            <View key={idx} style={{ flexDirection: 'row', marginBottom: 2 }}>
                                                <Text style={{ fontSize: 9, fontWeight: 'bold', width: 80 }}>{d.label}</Text>
                                                <Text style={{ fontSize: 9 }}>{d.value}</Text>
                                            </View>
                                        ))}
                                    </View>
                                );
                            })()}
                        </View>

                        {typeof hotelPassengerCount === 'number' && hotelPassengerCount > 0 && (
                            <View style={[styles.section, styles.marginBottom10px]}>
                                <Text style={[styles.littleText]}>
                                    Number of Passengers: {hotelPassengerCount}
                                </Text>
                            </View>
                        )}

                        {hotelDetailsColumns?.length > 0 && hotelDetailsRows?.length > 0 && (
                            <View style={[styles.section, styles.marginBottom10px]}>
                                <PdfTableComponent columns={hotelDetailsColumns} rows={hotelDetailsRows} />
                            </View>
                        )}

                        {hotelGuestColumns?.length > 0 && hotelGuestRows?.length > 0 && (
                            <>
                                <View style={[styles.section, styles.marginBottom10px]}>
                                    <PdfTableComponent columns={hotelGuestColumns} rows={hotelGuestRows} />
                                </View>
                                <View style={[styles.section, styles.marginBottom10px]}>
                                    <Text style={[styles.littleText]}>
                                        Total Guests: {hotelGuestRows.length}
                                    </Text>
                                </View>
                            </>
                        )}

                        {passengersTable}
                    </>
                );
            case TravelFileServicePreviewNamesEnum.GENERAL: {
                // Same unified style as hotel/package — title + details + breakdown
                const titleValue = pdfBodyBoxData?.[0]?.value || '';
                const detailItems = pdfBodyBoxData?.slice(1).filter((d: any) => d.label || d.value) || [];
                return (
                    <>
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.littleText, styles.marginBottom4px]}>
                                Please confirm the following reservation:
                            </Text>
                            <View style={{ marginBottom: 8 }}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline', marginBottom: 6 }}>
                                    <Text style={{ fontSize: 11, fontWeight: 'bold' }}>{titleValue}</Text>
                                </View>
                                {detailItems.map((d: any, idx: number) => (
                                    <View key={idx} style={{ flexDirection: 'row', marginBottom: 2 }}>
                                        <Text style={{ fontSize: 9, fontWeight: 'bold', width: 80 }}>{d.label}</Text>
                                        <Text style={{ fontSize: 9 }}>{d.value}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>

                        {hotelDetailsColumns?.length > 0 && hotelDetailsRows?.length > 0 && (
                            <View style={[styles.section, styles.marginBottom10px]}>
                                <PdfTableComponent columns={hotelDetailsColumns} rows={hotelDetailsRows} />
                            </View>
                        )}

                        {passengersTable}
                    </>
                );
            }
            case TravelFileServicePreviewNamesEnum.TOUR:
            case TravelFileServicePreviewNamesEnum.VISA:
                return (
                    <>
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.littleText, styles.marginBottom4px]}>
                                Please provide/arrange for the following service:
                            </Text>
                            <PdfTableComponent columns={voucherDetailsColumns} rows={voucherDetailsRows} />
                        </View>

                        {passengersTable}
                    </>
                );
            default:
                return <></>;
        }
    }, [
        passengersSummaryLine,
        pdfBodyBoxData,
        pdfTotalBoxData,
        priceComponentRows,
        travelFileServiceTypePreviewName,
        voucherDetailsColumns,
        voucherDetailsRows,
        voucherPassengersColumns,
        voucherPassengersRows,
        provideArrangeForText,
        hidePassengersTable,
        hotelDetailsColumns,
        hotelDetailsRows,
        hotelGuestColumns,
        hotelGuestRows,
        hotelPassengerCount
    ]);

    const isCommissionable = useMemo<boolean>(() => {
        return !!pdfTotalBoxData?.find(
            (item: any) => item.label.includes('Commission') && Number(item.value) > 0
        );
    }, [pdfTotalBoxData]);

    const defaultAmount: number = props.info?.amount || 0;
    const defaultCommission: number = props.info?.commission || 0;
    const defaultMarkup: number = props.info?.markup || 0;
    const defaultTax: number = props.info?.tax || 0;
    const defaultCurrencyCode: string = props.info?.currency?.code || '';

    const defaultPdfTotalBoxData: PdfTotalBoxDataItem[] = [
        { label: 'Amount', value: defaultAmount },
        { label: 'Commission', value: defaultCommission },
        { label: 'Markup', value: defaultMarkup },
        { label: 'Tax', value: defaultTax },
        {
            label: `Total (${defaultCurrencyCode})`,
            value: defaultAmount + defaultCommission + defaultMarkup + defaultTax
        }
    ];

    return (
        <Document title={`${voucherType} ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <PdfSupplierDetails
                                supplierName={supplierName}
                                supplierDetails={supplierDetails}
                            />
                            <PdfHeaderDetails
                                iata={companyInfo.iata}
                                issueDate={props.issueDate || createdAt}
                                dueDate={dueDate}
                                travelFileNumber={travelFileNumber}
                                travelFileFirst
                                supplierInvoiceNumber={supplierInvoiceNumber}
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                    </View>

                    <View style={{ ...styles.section, ...styles.alignCenter }} wrap={false}>
                        <Text style={{ ...styles.paymentStatus }}>{isPaid ? 'PAID' : ''}</Text>
                        <PdfMainDetails
                            topDetails={`${travelFileServiceTypePreviewName} ${voucherType} No. ${
                                voucherNumber || ''
                            }`}
                            bottomDetails={
                                code1code2?.code1
                                    ? `${code1code2.code1}${code1code2.code2 ? `-${code1code2.code2}` : ''}`
                                    : (props as any).info?.for || ''
                            }
                            withForLabel
                        />
                    </View>

                    {splitFromNote ? (
                        <View style={[styles.section, styles.alignCenter, styles.marginBottom10px]} wrap={false}>
                            <Text style={[styles.littleText, { fontWeight: 'bold' }]}>{splitFromNote}</Text>
                        </View>
                    ) : null}

                    {bodyDetails}
                    <PdfTotalBox
                        pdfTotalBoxData={pdfTotalBoxData ? pdfTotalBoxData : defaultPdfTotalBoxData}
                    />

                    {(travelFileServiceTypePreviewName === 'Guide' ||
                        travelFileServiceTypePreviewName === 'Package' ||
                        travelFileServiceTypePreviewName === 'General') && (
                        <View style={[styles.section, styles.marginBottom10px]} wrap={false}>
                            <Text style={[styles.littleText, { fontWeight: 'bold' }]}>
                                Our local representative:
                            </Text>
                            {detailsText ? (
                                <Text style={[styles.littleText]}>
                                    Details:{'   '}
                                    <Text style={{ fontWeight: 'bold' }}>{detailsText}</Text>
                                </Text>
                            ) : null}
                            {supplierAddressText ? (
                                <Text style={[styles.littleText]}>
                                    Address:{'  '}
                                    <Text style={{ fontWeight: 'bold' }}>{supplierAddressText}</Text>
                                </Text>
                            ) : null}
                        </View>
                    )}

                    <View style={[styles.section, styles.marginBottom10px]} wrap={false}>
                        <Text style={[styles.littleText]}>
                            This voucher is payable by{'  '}
                            <Text style={{ fontWeight: 'bold' }}>{companyInfo.name}</Text>
                        </Text>
                        <Text style={[styles.littleText]}>{companyInfo.phone}</Text>
                        <Text style={[styles.littleText]}>{companyInfo.website}</Text>
                        <Text style={[styles.littleText]}>
                            All extras should be charged directly to client
                        </Text>
                        {isCommissionable ? (
                            <Text style={[styles.littleText]}>This voucher is commissionable</Text>
                        ) : (
                            <></>
                        )}
                    </View>

                    <PdfRemarksSection body={remark} />

                    <View style={styles.section}>
                        <Text>Issued by: {userName}</Text>
                    </View>
                </View>

                <PdfFooter companyInfo={companyInfo} />
            </Page>
        </Document>
    );
};

export default memo(VoucherServicePdfDocument);
